/* Supply › Reports › Purchase › Purchase Orders
 * Purchase orders raised, by supplier / warehouse / status.
 * Data: GET /v1/purchase-orders (mapped to PO rows). */
import React, { useEffect, useMemo, useState } from "react";
import supplyService from "../../../Services/supplyService";
import { useWarehouse } from "../../Layout/WarehouseContext";
import { cbFmtINR, cbFmtNum, cbStatusVariant } from "../supplyData";
import {
  Button,
  Pill,
  Select,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  KpiBox,
  LoadingState,
  ErrorState,
  IconDownload,
} from "../supplyKit";
import { exportToXlsx } from "./reportUtils";
import { ReportBack } from "./reportShared";
import { PHARMACY_ROUTES } from "../../../Route/path";

const STATUSES = ["draft", "approved", "sent", "partially_received", "closed", "cancelled"];

const PurchaseOrders = () => {
  /* The store in scope, chosen once in the topbar. */
  const { code: wh } = useWarehouse();
  const [suppliers, setSuppliers] = useState([]);
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [status, setStatus] = useState("");

  useEffect(() => {
    let alive = true;
    supplyService
      .getSuppliers()
      .then((s) => alive && setSuppliers(s || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [reloadKey]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const params = { size: 500, sort: "poDate,desc" };
    /* Always scoped to the store selected in the topbar — for a head too, who now
       switches store there rather than reporting across all of them at once. */
    if (wh) params.warehouseId = wh;
    if (status) params.status = status;
    supplyService
      .getPurchaseOrders(params)
      .then((rows) => {
        if (!alive) return;
        setPos(Array.isArray(rows) ? rows : []);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load purchase orders.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [reloadKey, wh, status]);

  const supName = (id, fallback) => suppliers.find((s) => s.id === id)?.name || fallback || id;

  const totals = useMemo(
    () =>
      pos.reduce(
        (acc, p) => {
          acc.value += Number(p.value) || 0;
          acc.units += Number(p.orderedUnits) || 0;
          if (["draft", "approved", "sent", "partially_received"].includes(p.status)) acc.open += 1;
          return acc;
        },
        { value: 0, units: 0, open: 0 }
      ),
    [pos]
  );

  const handleExport = () => {
    const headers = ["PO #", "Date", "Supplier", "Warehouse", "Items", "Ordered units", "Value", "Expected", "Status"];
    const rows = pos.map((p) => [
      p.id,
      p.date || "",
      supName(p.supplier, p.supplierName),
      p.wh || "",
      p.items,
      p.orderedUnits,
      Number((Number(p.value) || 0).toFixed(2)),
      p.expectedDate || "",
      p.status,
    ]);
    exportToXlsx("purchase-orders.xlsx", headers, rows, "Purchase orders");
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <ReportBack to={PHARMACY_ROUTES.PURCHASE_REPORT} label="Purchase Reports" />
      <SectionHeader
        title="Purchase Orders"
        sub="Purchase orders raised, by status and supplier"
        actions={
          <Button icon={<IconDownload size={14} />} onClick={handleExport} disabled={!pos.length}>
            Export
          </Button>
        }
      />

      <ToolbarBar>
        <Select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replace(/_/g, " ")}
            </option>
          ))}
        </Select>
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {cbFmtNum(pos.length)} POs
        </div>
      </ToolbarBar>

      <div className="grid grid-cols-1 sm:grid-cols-3" style={{ display: "grid", gap: 10 }}>
        <KpiBox label="Purchase orders" value={cbFmtNum(pos.length)} variant="ink" />
        <KpiBox label="Open POs" value={cbFmtNum(totals.open)} variant="amber" />
        <KpiBox label="PO value" value={cbFmtINR(totals.value)} variant="green" />
      </div>

      {loading ? (
        <LoadingState label="Loading purchase orders…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <Table
          columns={[
            { label: "PO #" },
            { label: "Date" },
            { label: "Supplier" },
            { label: "Wh" },
            { label: "Items", align: "right" },
            { label: "Ordered", align: "right" },
            { label: "Value", align: "right" },
            { label: "Expected" },
            { label: "Status" },
          ]}
        >
          {pos.length === 0 && (
            <tr>
              <Td colSpan={9} style={{ textAlign: "center", color: "var(--fg-muted)", height: 80 }}>
                No purchase orders found.
              </Td>
            </tr>
          )}
          {pos.map((p) => (
            <Tr key={p.poId || p.id}>
              <Td mono style={{ fontWeight: 600 }}>
                {p.id}
              </Td>
              <Td mono>{p.date}</Td>
              <Td>{supName(p.supplier, p.supplierName)}</Td>
              <Td>
                <Pill variant="neutral">{p.wh}</Pill>
              </Td>
              <Td mono right>
                {p.items}
              </Td>
              <Td mono right>
                {cbFmtNum(p.orderedUnits)}
              </Td>
              <Td mono right style={{ fontWeight: 600 }}>
                {cbFmtINR(p.value)}
              </Td>
              <Td mono style={{ color: "var(--fg-muted)" }}>
                {p.expectedDate}
              </Td>
              <Td>
                <Pill variant={cbStatusVariant(p.status)} dot>
                  {p.status.replace(/_/g, " ")}
                </Pill>
              </Td>
            </Tr>
          ))}
        </Table>
      )}
    </div>
  );
};

export default PurchaseOrders;
